import React, { useState, useEffect } from 'react';
import FloorList from '../UI/FloorList';

const MainPage = ({
  currentElevatorData,
  selectedCurrentFloor,
  selectedDestinationFloor,
  currentSelectionMode,
  handicap,
  setHandicap,
  onFloorClick,
  onCallElevator,
  onSwapFloors,
  onCurrentFloorClick,
  onDestinationFloorClick,
  onPlaceholderCancel,
  currentRegNumber,
  getInstructionText,
  areBothFloorsSelected,
  shouldShowCallReady
}) => {
  // localStorageから初期値を取得する関数
  const getInitialRateLimitState = () => {
    const rateLimitUntil = localStorage.getItem('rate_limit_until');
    if (!rateLimitUntil) return { isRateLimited: false, remainingTime: 0, progress: 0 };

    const remaining = parseInt(rateLimitUntil) - Date.now();
    if (remaining > 0) {
      // console.log('Initial rate limit state from localStorage:', remaining, 'ms');
      return {
        isRateLimited: true,
        remainingTime: remaining,
        progress: ((60000 - remaining) / 60000) * 100
      };
    } else {
      // 期限切れの場合はlocalStorageをクリア
      // console.log('Initial rate limit expired, clearing');
      localStorage.removeItem('rate_limit_until');
      return { isRateLimited: false, remainingTime: 0, progress: 0 };
    }
  };

  const initialState = getInitialRateLimitState();
  const [isRateLimited, setIsRateLimited] = useState(initialState.isRateLimited);
  const [remainingTime, setRemainingTime] = useState(initialState.remainingTime);
  const [progress, setProgress] = useState(initialState.progress);

  const instructionText = getInstructionText();

  // エレベータ未選択時 or 車イス非対応エレベータではhandicapをfalseにリセット
  useEffect(() => {
    if (!currentRegNumber || !currentElevatorData || !currentElevatorData.wheelchair) {
      setHandicap(false);
    }
  }, [currentRegNumber, currentElevatorData, setHandicap]);

  useEffect(() => {
    if (!isRateLimited || remainingTime <= 0) {
      // レート制限が終了したらlocalStorageをクリア
      // ただし、isRateLimitedがtrueからfalseになった場合のみクリア
      if (isRateLimited && remainingTime <= 0) {
        // console.log('Rate limit timer finished, clearing localStorage');
        localStorage.removeItem('rate_limit_until');
        setIsRateLimited(false);
      }
      return;
    }

    const interval = setInterval(() => {
      setRemainingTime(prev => {
        const newTime = prev - 100;
        if (newTime <= 0) {
          // console.log('Rate limit expired via interval');
          localStorage.removeItem('rate_limit_until');
          setIsRateLimited(false);
          setProgress(100);
          return 0;
        }
        setProgress(((60000 - newTime) / 60000) * 100);
        return newTime;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isRateLimited, remainingTime]);

  const handleCallButtonClick = async () => {
    if (isRateLimited || !areBothFloorsSelected() || !currentRegNumber) return;

    const result = await onCallElevator(selectedCurrentFloor, selectedDestinationFloor, handicap);

    // レート制限エラーの場合
    if (result && !result.success && result.isRateLimited) {
      setIsRateLimited(true);
      setRemainingTime(60000); // 60秒
      setProgress(0);
    }
  };

  const getCallButtonClass = () => {
    const bothFloorsSelected = areBothFloorsSelected();
    const activeColor = handicap ? 'ux-bg-blue' : 'ux-bg-red';

    if (isRateLimited) {
      return `mx-[calc(100vw/12)] h-[calc(100svh/24*2)] flex text-gray-500 font-bold items-center ux-rounded text-center justify-center mb-[calc(100svh/45*2)] cursor-not-allowed relative overflow-hidden`;
    } else if (bothFloorsSelected && currentRegNumber) {
      return `mx-[calc(100vw/12)] h-[calc(100svh/24*2)] flex ${activeColor} text-white font-bold items-center ux-rounded text-center justify-center mb-[calc(100svh/45*2)] cursor-pointer relative overflow-hidden`;
    } else {
      return `mx-[calc(100vw/12)] h-[calc(100svh/24*2)] flex ux-bg-gray text-gray-500 font-bold items-center ux-rounded text-center justify-center mb-[calc(100svh/45*2)] cursor-default relative overflow-hidden`;
    }
  };

  const getCallButtonStyle = () => {
    if (!isRateLimited) return {};

    // ux-bg-blue: #0055DC, ux-bg-red: #D70024, ux-bg-gray: #D1D5DB
    const activeColor = handicap ? '#0055DC' : '#D70024';
    const grayColor = '#D1D5DB';

    return {
      background: `conic-gradient(from 0deg, ${activeColor} ${progress}%, ${grayColor} ${progress}%)`
    };
  };

  return (
    <div className="w-full bg-white swiper-slide-rd">
      <div className="w-full bg-white rounded-2xl shadow-lg flex flex-col relative">
        {/* Instruction Text */}
        <div className="h-[calc(100svh/24)] relative my-[calc(100svh/36)] mx-[calc(100vw/12)]">
          <div className="ux-text-bold text-gray-600 text-left mb-[calc(100svh/36)] mt-[calc(100svh/60)]">
            {instructionText}
          </div>
        </div>

        {/* Wheelchair Mode Toggle - wheelchair 対応エレベータのみ表示 */}
        {currentElevatorData?.wheelchair && (
          <button
            onClick={() => setHandicap(!handicap)}
            className={`border absolute h-[calc(100svh/72*5)] w-[calc(100vw/36*5)] right-0 top-[calc(100svh/36)] ux-wheel-shadow z-10 rounded-tl-lg rounded-bl-lg ${handicap ? 'ux-bg-blue' : ''} ${!currentRegNumber ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            id="color-switcher"
            disabled={!currentRegNumber}
          >
            <img
              src={handicap ? './assets/static/W_ON.png' : './assets/static/W_OFF.png'}
              className="h-[calc(100svh/72*3)] w-[calc(100vw/36*3)] object-contain mx-[calc(100vw/36)] my-[calc(100svh/72)]"
              alt="Wheelchair Mode"
            />
          </button>
        )}

        {/* Floor Selection Area */}
        <div className="flex-grow flex flex-col items-center justify-center h-[calc(100svh/24*12)] mb-[calc(100svh/24)] mt-[calc(100svh/30)] mx-[calc(100vw/12)] ux-border-l ux-border-r border-gray-300">
          <FloorList
            floors={currentElevatorData?.floors || []}
            selectedCurrentFloor={selectedCurrentFloor}
            selectedDestinationFloor={selectedDestinationFloor}
            currentSelectionMode={currentSelectionMode}
            handicap={handicap}
            onFloorClick={onFloorClick}
            onPlaceholderCancel={onPlaceholderCancel}
            onSwapFloors={onSwapFloors}
            currentRegNumber={currentRegNumber}
          />
        </div>

        {/* Call Button with Clock-style Progress */}
        <button
          onClick={handleCallButtonClick}
          className={getCallButtonClass()}
          style={getCallButtonStyle()}
          disabled={isRateLimited}
        >
          <span style={{ fontSize: '1.1rem', position: 'relative', zIndex: 1, color: 'white' }}>
            {/* {isRateLimited ? `あと${Math.ceil(remainingTime / 1000)}秒` : '呼ぶ'} */}
            呼ぶ
          </span>
        </button>
      </div>
    </div>
  );
};

export default MainPage;